import { useCallback, useEffect, useId, useState } from 'react';
import { create } from 'zustand';
import { useDataFreshnessStore, type DataDomain } from './stores';
import { useAuthStore } from './authStore';

// ─── Active-edit registry + conflict guard ───────────────────────
// Tracks which entities the user is currently EDITING (with unsaved changes) so an
// edit surface can warn — via EditConflictBanner — when the item it's editing was
// just changed by someone else (overwrite risk). The registry is the durable home
// for the dirty flag (and a seam for a future global "you have unsaved edits"
// watcher); the conflict math lives in useEditConflictGuard, reading the realtime
// freshness store. Self-changes (your own MCP write / another of your tabs) never
// alarm; the local/auth-off solo user is never alarmed at all.

export type EditEntity = 'backlog' | 'ticket' | 'topic' | 'request' | 'feedback';

// 'feedback' is keyed by the BOARD id, not the comment's — that is the id the
// change events carry, so it is the only granularity the freshness store has. A
// change anywhere on the board therefore guards a comment being edited on it,
// which is the safe direction to be wrong in. (b-01911)
const ENTITY_TO_DOMAIN: Record<EditEntity, DataDomain> = {
  backlog: 'azure', ticket: 'zoho', topic: 'discourse', request: 'requests', feedback: 'feedback',
};

interface ActiveEdit { entity: EditEntity; id: string; dirty: boolean; startedAt: number; }

interface ActiveEditState {
  /** key = `${entity}:${id}:${instanceId}` */
  edits: Record<string, ActiveEdit>;
  register: (key: string, e: ActiveEdit) => void;
  setDirty: (key: string, dirty: boolean) => void;
  unregister: (key: string) => void;
}

export const useActiveEditStore = create<ActiveEditState>()((set) => ({
  edits: {},
  register: (key, e) => set((s) => ({ edits: { ...s.edits, [key]: e } })),
  setDirty: (key, dirty) =>
    set((s) => (s.edits[key] && s.edits[key].dirty !== dirty
      ? { edits: { ...s.edits, [key]: { ...s.edits[key], dirty } } }
      : s)),
  unregister: (key) => set((s) => { const { [key]: _drop, ...rest } = s.edits; return { edits: rest }; }),
}));

/** Any edit surface currently has unsaved changes. Safe to call outside React. */
export function hasDirtyEdit(): boolean {
  return Object.values(useActiveEditStore.getState().edits).some((e) => e.dirty);
}

/**
 * Run `cb` once, the next time the registry transitions to having NO dirty edit
 * (fires immediately if nothing is dirty right now). Returns an unsubscribe fn.
 * Lets a hard reload (e.g. a service-worker update) wait until the user's
 * in-progress edit is saved/closed, so a deploy never discards a half-typed form.
 */
export function onEditsClear(cb: () => void): () => void {
  if (!hasDirtyEdit()) { cb(); return () => {}; }
  const unsub = useActiveEditStore.subscribe((state) => {
    if (!Object.values(state.edits).some((e) => e.dirty)) {
      unsub();
      cb();
    }
  });
  return unsub;
}

export interface EditConflictInfo {
  /** Someone ELSE changed THIS id while it's being edited dirty. */
  conflict: boolean;
  /** Acting user id of the conflicting change (null = unattributed). */
  byActor: string | null;
  /** Soft signal (id-less / reconnect / unattributed) → "may have changed" wording. */
  coarse: boolean;
  /** The conflicting change deleted the item. */
  deleted: boolean;
  /** Silence the current conflict until a NEWER change arrives (drives "Keep editing"
   *  and the post-reload reset so re-editing the just-loaded data doesn't re-alarm). */
  acknowledge: () => void;
}

/**
 * Edit-conflict guard for an edit surface. Registers `{entity,id}` while mounted,
 * tracks `isDirty`, and reports whether the edited item changed under the user.
 */
export function useEditConflictGuard(
  entity: EditEntity,
  id: string | number | null | undefined,
  isDirty: boolean,
): EditConflictInfo {
  const instanceId = useId();
  const domain = ENTITY_TO_DOMAIN[entity];
  const idStr = id != null ? String(id) : null;
  const key = idStr ? `${entity}:${idStr}:${instanceId}` : null;
  const entityKey = idStr ? `${domain}:${idStr}` : null;

  const register = useActiveEditStore((s) => s.register);
  const setDirty = useActiveEditStore((s) => s.setDirty);
  const unregister = useActiveEditStore((s) => s.unregister);

  const entityRec = useDataFreshnessStore((s) => (entityKey ? s.entities[entityKey] : undefined));
  const coarseVersion = useDataFreshnessStore((s) => s.versions[domain]);
  const coarseActor = useDataFreshnessStore((s) => s.lastActorByDomain[domain] ?? null);

  // Acknowledged baseline: a change is only a conflict if it's NEWER than this.
  const [ack, setAck] = useState({ cursor: 0, coarse: 0 });

  // (Re)baseline + (un)register when the edit target changes.
  useEffect(() => {
    if (!key || !entityKey) return;
    const st = useDataFreshnessStore.getState();
    setAck({ cursor: st.entities[entityKey]?.cursor ?? 0, coarse: st.versions[domain] });
    register(key, { entity, id: idStr as string, dirty: isDirty, startedAt: Date.now() });
    return () => unregister(key);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, entityKey, domain]);

  useEffect(() => { if (key) setDirty(key, isDirty); }, [key, isDirty, setDirty]);

  const acknowledge = useCallback(() => {
    const st = useDataFreshnessStore.getState();
    setAck({ cursor: (entityKey ? st.entities[entityKey]?.cursor : 0) ?? 0, coarse: st.versions[domain] });
  }, [entityKey, domain]);

  const me = useAuthStore.getState().user?.id ?? null;
  const isSelf = (actor: string | null) => actor != null && me != null && String(actor) === String(me);

  // No alarm unless actively editing as a known (non-local) user.
  if (!isDirty || me === null || !idStr) {
    return { conflict: false, byActor: null, coarse: false, deleted: false, acknowledge };
  }

  // Precise — this exact id changed since we acknowledged.
  if (entityRec && entityRec.cursor > ack.cursor) {
    if (entityRec.actorUserId == null) {
      // Unattributed (system/sync OR the Discourse null-echo) → soft, non-accusatory.
      return { conflict: true, byActor: null, coarse: true, deleted: entityRec.change === 'deleted', acknowledge };
    }
    if (!isSelf(entityRec.actorUserId)) {
      return { conflict: true, byActor: entityRec.actorUserId, coarse: false, deleted: entityRec.change === 'deleted', acknowledge };
    }
    // self precise change → no alarm
  }
  // Coarse — an id-less domain change (reconnect / large sync) by not-me.
  if (coarseVersion > ack.coarse && !isSelf(coarseActor)) {
    return { conflict: true, byActor: coarseActor, coarse: true, deleted: false, acknowledge };
  }
  return { conflict: false, byActor: null, coarse: false, deleted: false, acknowledge };
}
